import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { 
  FiHome, FiBook, FiUsers, FiHeart, FiDollarSign, 
  FiGlobe, FiUser, FiStar, FiActivity, FiBriefcase, FiHash 
} from "react-icons/fi";

import { getZoneColor, ZONE_ORDER } from "../../utils/zoneUtils";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface WellbeingRow {
  catId:    number;
  catName:  string;
  avgScore: number;
  logCount: number;
  zone:     string;
  status:   string;
}

type WellbeingPeriod = "current-month" | "last-month" | "quarter" | "year" | "all";

// ─── Zone config (matching screenshot) ────────────────────────────────────────
const STATUS_CONF: Record<string, { bg: string; text: string }> = {
  "Excellent":    { bg: "#dcfce7", text: "#15803d" },
  "Stable":       { bg: "#dbeafe", text: "#1d4ed8" },
  "Needs Review": { bg: "#fee2e2", text: "#b91c1c" },
};
const statusConf = (s: string) => STATUS_CONF[s] ?? { bg: "#f3f4f6", text: "#374151" };

// ─── Category icons (Professional SVG) ───────────────────────────────────────

const CAT_ICONS: Record<string, React.ReactNode> = {
  "Teachers":                           <FiBriefcase />,
  "Academics":                          <FiBook />,
  "Family":                             <FiHome />,
  "Friends":                            <FiUsers />,
  "Money":                              <FiDollarSign />,
  "Health & Fitness":                   <FiHeart />,
  "Self":                               <FiUser />,
  "Society/ Environment/ Surroundings": <FiGlobe />,
  "Hobby":                              <FiStar />,
  "Sports":                             <FiActivity />,
};
const catIcon = (name: string) => CAT_ICONS[name.trim()] ?? <FiHash />;

// ─── Period options ────────────────────────────────────────────────────────────

// PERIODS array removed as StudentDashboard handles it

const MAX_RADIUS = 150;
const NUM_ZONES = ZONE_ORDER.length; // 7

// ORBIT DESIGN: Outer ring = Green (GOOD/SAFE), Inner center = Dark Red (BAD/DANGER)
// ZONE_ORDER[0]=Green (best) → biggest radius (outermost visible ring)
// ZONE_ORDER[6]=Dark Red (worst) → smallest radius (center)
const ZONE_RINGS = [...ZONE_ORDER].map((zone, i) => {
  // Green (i=0) → r=MAX_RADIUS, Dark Red (i=6) → r=MAX_RADIUS/7
  const fraction = (NUM_ZONES - i) / NUM_ZONES;
  return { zone, r: MAX_RADIUS * fraction };
});
// Paint largest first so smaller circles cover its center:
// Green (r=150) first → each zone covers the inner part → outer band = Green, center = Dark Red

const CX = 175;
const CY = 175;

const polarToXY = (angle: number, r: number) => ({
  x: CX + r * Math.cos(angle),
  y: CY + r * Math.sin(angle),
});

// Score → radius: HIGH score (good) = BIG r (outer/green), LOW score (bad) = SMALL r (inner/red)
// score=7 → r=MAX_RADIUS (outer green ring), score=1 → r=0 (center dark red)
const rForScore = (score: number | null): number | null => {
  if (score == null || score < 1) return null;
  const clamped = Math.max(1, Math.min(7, score));
  return MAX_RADIUS * ((clamped - 1) / 6);
};

// ─── Radar Chart ───────────────────────────────────────────────────────────────

interface RadarChartProps { 
  rows: WellbeingRow[]; 
  compareRows?: WellbeingRow[];
  highlightIdx: number | null; 
  onHover: (i: number | null) => void; 
}

const RadarChart: React.FC<RadarChartProps> = ({ rows, compareRows, highlightIdx, onHover }) => {
  if (!rows.length) return null;

  const n = rows.length;
  if (n === 0) return null;

  const angleStep = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2;

  // Map ALL categories to their spokes and label positions
  const allPoints = rows.map((row, i) => {
    const angle = startAngle + i * angleStep;
    const r = rForScore(row.logCount > 0 ? row.avgScore : null);
    const hasData = r !== null;
    return { row, angle, r, hasData, ...polarToXY(angle, r ?? 0) };
  });

  const validPoints = allPoints.filter(p => p.hasData);

  return (
    <svg viewBox={`0 0 ${CX * 2} ${CY * 2}`} width="100%" style={{ maxWidth: 360, display: "block" }}>
      <defs>
        <filter id="wb-shadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="6" floodColor="#1ecab8" floodOpacity="0.2" />
        </filter>
      </defs>

      {/* Concentric zone rings — drawn as non-overlapping donuts to prevent opacity stacking */}
      {[...ZONE_RINGS].map((ring, i) => {
        const zc = getZoneColor(ring.zone);
        const rOuter = ring.r;
        const rInner = i < ZONE_RINGS.length - 1 ? ZONE_RINGS[i+1].r : 0;
        const strokeW = rOuter - rInner;
        const rCenter = rInner + strokeW / 2;

        return (
          <g key={ring.zone}>
            {/* Colored zone band */}
            <circle
              cx={CX} cy={CY} r={rCenter}
              fill="none"
              stroke={zc.color} 
              strokeWidth={strokeW + 0.5} /* tiny overlap to prevent hairline gaps */
              opacity={0.3}
            />
            {/* White separator line */}
            <circle
              cx={CX} cy={CY} r={rOuter}
              fill="none"
              stroke="#ffffff"
              strokeWidth={1}
            />
          </g>
        );
      })}

      {/* Axis spokes — reach to the outermost ring (Green = MAX_RADIUS) */}
      {allPoints.map((p, i) => {
        const outerP = polarToXY(p.angle, MAX_RADIUS);
        return (
          <line
            key={`spoke-${i}`}
            x1={CX} y1={CY} x2={outerP.x} y2={outerP.y}
            stroke="#ffffff" strokeWidth={1} strokeDasharray="4 3" opacity={0.8}
          />
        );
      })}

      {/* Data polygon with NO fill, connecting only valid points */}
      {validPoints.length > 1 && (
        <polygon
          points={validPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")}
          fill="none"
          stroke="#1ecab8" strokeWidth={2.5}
          strokeLinejoin="round"
        />
      )}

      {/* Compare polygon (dashed, semi-transparent grey) */}
      {compareRows && (() => {
        const compareAllPoints = compareRows.map((row, i) => {
          const angle = startAngle + i * angleStep;
          const r = rForScore(row.logCount > 0 ? row.avgScore : null);
          return { row, angle, r, hasData: r !== null, ...polarToXY(angle, r ?? 0) };
        });
        const compareValidPoints = compareAllPoints.filter(p => p.hasData);
        if (compareValidPoints.length < 2) return null;
        return (
          <polygon
            points={compareValidPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")}
            fill="none"
            stroke="rgba(156,163,175,0.9)" strokeWidth={2}
            strokeDasharray="5 4"
            strokeLinejoin="round"
          />
        );
      })()}

      {/* Data point dots (only for valid points) */}
      {validPoints.map((p, i) => {
        const isHover = highlightIdx === rows.indexOf(p.row);
        return (
          <g key={`dot-${i}`}
            onMouseEnter={() => onHover(rows.indexOf(p.row))}
            onMouseLeave={() => onHover(null)}
            style={{ cursor: "pointer" }}
          >
            <circle
              cx={p.x} cy={p.y}
              r={isHover ? 6.5 : 4.5}
              fill="#1ecab8"
              stroke="#fff"
              strokeWidth={2}
              style={{ transition: "r 0.15s" }}
            />
          </g>
        );
      })}

      {/* Category labels (drawn for ALL categories) */}
      {allPoints.map((p, i) => {
        const labelR  = MAX_RADIUS + 18; // just outside the outermost Green ring
        const lp      = polarToXY(p.angle, labelR);
        const anchor  = lp.x > CX + 10 ? "start" : lp.x < CX - 10 ? "end" : "middle";
        const isHover = highlightIdx === rows.indexOf(p.row);

        return (
          <g key={`label-${i}`}
            onMouseEnter={() => onHover(rows.indexOf(p.row))}
            onMouseLeave={() => onHover(null)}
            style={{ cursor: "pointer" }}
          >
            <text
              x={lp.x} y={lp.y + 4}
              textAnchor={anchor} 
              fontSize={isHover ? 8 : 7.5}
              fontWeight={800}
              fill={isHover ? "#1ecab8" : "#8ca3b5"}
              style={{ textTransform: "uppercase", letterSpacing: "0.05em", transition: "all 0.15s" }}
            >
              {p.row.catName}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

interface Props {
  studentId: number | string;
  primaryPeriod: WellbeingPeriod;
  comparePeriod?: WellbeingPeriod;
  showCompare?: boolean;
  primaryStartDate?: string;
  primaryEndDate?: string;
  compareStartDate?: string;
  compareEndDate?: string;
}

const WellbeingAnalysis: React.FC<Props> = ({ 
  studentId, primaryPeriod, comparePeriod, showCompare,
  primaryStartDate, primaryEndDate, compareStartDate, compareEndDate
}) => {
  const [data,         setData]         = useState<WellbeingRow[]>([]);
  const [compareData,  setCompareData]  = useState<WellbeingRow[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);
  const [period,       setPeriod]       = useState<WellbeingPeriod>(primaryPeriod ?? "all");
  const [highlightIdx, setHighlightIdx] = useState<number | null>(null);

  useEffect(() => { setPeriod(primaryPeriod ?? "all"); }, [primaryPeriod]);

  const fetchData = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      setError(null);
      const promises = [
        axios.get(`${API_BASE_URL}/student-mood-logs/wellbeing/${studentId}`, { 
          params: { 
            period, 
            ...(primaryStartDate && { startDate: primaryStartDate }),
            ...(primaryEndDate && { endDate: primaryEndDate })
          } 
        }),
      ];
      if (showCompare && comparePeriod) {
        promises.push(
          axios.get(`${API_BASE_URL}/student-mood-logs/wellbeing/${studentId}`, { 
            params: { 
              period: comparePeriod,
              ...(compareStartDate && { startDate: compareStartDate }),
              ...(compareEndDate && { endDate: compareEndDate })
            } 
          })
        );
      }
      const results = await Promise.all(promises);
      setData(results[0].data.data ?? []);
      if (showCompare && comparePeriod && results.length > 1) {
        setCompareData(results[1].data.data ?? []);
      } else {
        setCompareData([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load wellbeing data");
    } finally {
      setLoading(false);
    }
  }, [studentId, period, showCompare, comparePeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-9 h-9 border-[3px] border-emerald-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-gray-400 font-medium">Loading wellbeing data…</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <p className="text-sm font-semibold text-red-500">{error}</p>
      <button onClick={fetchData} className="text-xs text-emerald-600 font-bold hover:underline">Retry</button>
    </div>
  );

  if (!data.length) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center">
        <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-400">No mood logs found</p>
        <p className="text-xs text-gray-300 mt-1">No data available for the selected period.</p>
      </div>
    </div>
  );


  // All rows show in the table (including N/A ones)
  const tableRows = data;

  return (
    <div className="w-full">

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="mb-5">
        <h3 className="text-sm font-bold text-gray-800">Wellbeing Analysis (Orbit)</h3>
        <p className="text-[11px] text-gray-400 mt-0.5">Avg. joyfulness per life circle mapped to zone</p>
      </div>

      {/* ── Two-column layout ────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* LEFT: Orbit radar — draw all categories (N/A data plots at center) */}
        <div className="w-full lg:w-auto flex flex-col items-center shrink-0">
          <RadarChart rows={data} compareRows={compareData.length > 0 ? compareData : undefined} highlightIdx={highlightIdx} onHover={setHighlightIdx} />
          {/* Zone ring legend */}
          <div className="flex flex-wrap justify-center gap-2 mt-3">
            {ZONE_RINGS.map(({ zone }) => {
              const zc = getZoneColor(zone);
              return (
                <div key={zone} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: zc.color }} />
                  <span className="text-[10px] text-gray-500 font-medium">{zone}</span>
                </div>
              );
            })}
          </div>
          {/* Chart legend: primary vs compare lines */}
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <svg width="22" height="8"><polyline points="0,4 22,4" fill="none" stroke="#1ecab8" strokeWidth="2.5" strokeLinecap="round" /></svg>
              <span className="text-[10px] text-gray-500 font-medium">Current</span>
            </div>
            {compareData.length > 0 && (
              <div className="flex items-center gap-1.5">
                <svg width="22" height="8"><polyline points="0,4 22,4" fill="none" stroke="rgba(156,163,175,0.9)" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" /></svg>
                <span className="text-[10px] text-gray-400 font-medium">Compare</span>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Category table */}
        <div className="flex-1 w-full overflow-x-auto rounded-xl border border-gray-100 shadow-sm">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">CATEGORY</th>
                <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">SCORE (X/7)</th>
                {compareData.length > 0 && (
                  <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">VS COMPARE</th>
                )}
                <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">ZONE</th>
                <th className="px-4 py-3 text-center text-[11px] font-bold text-gray-400 uppercase tracking-wider">STATUS</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => {
                const zc      = getZoneColor(row.zone);
                const sc      = statusConf(row.status);
                const isHover = highlightIdx === i;

                return (
                  <tr
                    key={row.catId}
                    className="transition-colors"
                    style={{ borderBottom: "1px solid #f3f4f6", background: isHover ? "#f8fffe" : "transparent" }}
                    onMouseEnter={() => setHighlightIdx(i)}
                    onMouseLeave={() => setHighlightIdx(null)}
                  >
                    {/* Category */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-8 h-8 rounded-xl flex items-center justify-center text-base flex-shrink-0 shadow-sm"
                          style={{ background: `${zc.color}22`, color: zc.color }}
                        >
                          {catIcon(row.catName)}
                        </div>
                        <div>
                          <span className="font-semibold text-gray-800 text-sm">{row.catName}</span>
                          <span className="ml-1.5 text-[10px] text-gray-400">({row.logCount} log{row.logCount !== 1 ? "s" : ""})</span>
                        </div>
                      </div>
                    </td>

                    {/* Score with bar */}
                    <td className="px-4 py-3.5 text-center">
                      {row.logCount > 0 ? (
                        <div className="flex flex-col items-center gap-1.5">
                          <span className="font-bold text-gray-800">{row.avgScore.toFixed(1)}/7</span>
                          <div className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${(row.avgScore / 7) * 100}%`, background: zc.color, transition: "width 0.6s ease" }}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-300 font-semibold text-sm">0.0/7</span>
                      )}
                    </td>

                    {/* VS Compare diff column */}
                    {compareData.length > 0 && (() => {
                      const cRow = compareData.find(c => c.catId === row.catId || c.catName === row.catName);
                      if (!cRow || cRow.logCount === 0 || row.logCount === 0) {
                        return <td className="px-4 py-3.5 text-center"><span className="text-gray-300 text-xs">—</span></td>;
                      }
                      const diff = row.avgScore - cRow.avgScore;
                      const absDiff = Math.abs(diff).toFixed(1);
                      return (
                        <td className="px-4 py-3.5 text-center">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                            style={{
                              background: diff > 0 ? "#dcfce7" : diff < 0 ? "#fee2e2" : "#f3f4f6",
                              color: diff > 0 ? "#15803d" : diff < 0 ? "#b91c1c" : "#6b7280",
                            }}
                          >
                            {diff > 0 ? `↑ +${absDiff}` : diff < 0 ? `↓ -${absDiff}` : "— 0.0"}
                          </span>
                        </td>
                      );
                    })()}

                    {/* Zone badge */}
                    <td className="px-4 py-3.5 text-center">
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold"
                        style={{ background: `${zc.color}22`, color: zc.color }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: zc.color }} />
                        {row.zone}
                      </span>
                    </td>

                    {/* Status badge */}
                    <td className="px-4 py-3.5 text-center">
                      <span
                        className="inline-block px-3 py-1 rounded-full text-[11px] font-bold"
                        style={{ background: sc.bg, color: sc.text }}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            {(["Excellent", "Stable", "Needs Review"] as const).map(s => {
              const count = data.filter(r => r.status === s).length;
              const sc    = statusConf(s);
              return (
                <div key={s} className="flex items-center gap-2 px-2.5 py-1 rounded-lg" style={{ background: sc.bg }}>
                  <span className="text-xs font-bold" style={{ color: sc.text }}>{count}</span>
                  <span className="text-xs font-medium text-gray-600">{s}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellbeingAnalysis;
